import React from "react";
import { useTranslation } from 'react-i18next';
import { Box, Button, Menu, MenuItem as MuiMenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { actionButtonStyles } from "../styles/actionButton";
import { useNavigate } from "react-router-dom";
import { smallBold } from "../styles/typography";
import DraftsIcon from "@mui/icons-material/Drafts";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";

import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Static translation mappings for options button
const OPTIONS_TRANSLATIONS = {
  en: "Options",
  nl: "Opties"
};

const translateOptions = (i18n) => {
  return OPTIONS_TRANSLATIONS[i18n.language] || OPTIONS_TRANSLATIONS.en;
};
const BreadcrumbSection = ({
  file,
  currentTab,
  financeActiveTab,
  handleButtonClick,
  handleMenuClick,
  anchorEl,
  handleMenuClose,
  handleCreateAppointment,
  handleCreateHearing,
  handleOpenNotes,
  handleMenuOpen,
  handleRegisterClientPayment,
  handleCreateInvoice,
  handleCreateCredit,
  handleCreateProvisionNote,
  showDrafts,
  setShowDrafts,
}) => {
  const { t, i18n } = useTranslation(['common', 'buttons', 'pages', 'navigation']);
  const breadcrumbs = [{ label: t('navigation:files'), href: "/files" }];
  const navigate = useNavigate();
  const { files } = useSelector((state) => state.fileList);
  const { id } = useParams();
  
  // Add state for document dropdown menu
  const [documentMenuAnchorEl, setDocumentMenuAnchorEl] = React.useState(null);
  
  const handleDocumentMenuOpen = (event) => {
    setDocumentMenuAnchorEl(event.currentTarget);
  };
  
  const handleDocumentMenuClose = () => {
    setDocumentMenuAnchorEl(null);
  };
  
  const handleUploadDocument = () => {
    handleButtonClick();
    handleDocumentMenuClose();
  };
  
  const handleSendEmail = () => {
    navigate("/send-email", { state: { caseId: id } });
    handleDocumentMenuClose();
  };
  
  const handleUploadFromTemplate = () => {
    navigate("/documents/upload-from-template", { state: { caseId: id } });
    handleDocumentMenuClose();
  };

  const handleCreateTimesheet = () => {
    const currentFile = files.find((file) => file._id === id);
    console.log(currentFile);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate("/timesheets/bulk/create", { state: { caseId: id, owner } });
  };

  const handleOptions = () => {
    const currentUrl = window.location.pathname;
    const newUrl = `${currentUrl}/options`;

    const currentFile = files.find((file) => file._id === id);
    console.log(currentFile);
    const owner = currentFile ? currentFile.officeMembers[0].member : null;
    navigate(newUrl, { state: { caseId: id, owner } });
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <BreadcrumbNav
        title={file?.fileNo + " " + file?.name}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />
      {currentTab === "timesheets" ||
      currentTab === "tasks" ||
      currentTab === "disbursements" ||
      currentTab === "documents" ||
      currentTab === "emails" ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          {currentTab === "timesheets" ? (
            <Box>
              <Button
                variant="text"
                onClick={handleOptions}
                sx={{
                  textTransform: "none",
                  mr: 2,
                  ...smallBold,
                  color: "#2B80B6",
                  fontFamily:
                    "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                  fontSize: "11px",
                  fontWeight: 400,
                }}
              >
                {translateOptions(i18n)}
              </Button>
              <Button
                variant="text"
                onClick={handleCreateTimesheet}
                sx={{
                  textTransform: "none",
                  mr: 2,
                  ...smallBold,
                  color: "#2B80B6",
                  fontFamily:
                    "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                  fontSize: "11px",
                  fontWeight: 400,
                }}
              >
                {t('pages:timesheets.create_bulk')}
              </Button>
            </Box>
          ) : null}
          {currentTab === "documents" ? (
            <>
              <Button
                onClick={handleDocumentMenuOpen}
                endIcon={<ArrowDropDownIcon />}
                startIcon={
                  <Box display="flex" alignItems="center">
                    <AddIcon />
                    <Box ml={1} />
                  </Box>
                }
                sx={actionButtonStyles}
              >
                {t('pages:documents.new_document')}
              </Button>
              <Menu
                anchorEl={documentMenuAnchorEl}
                open={Boolean(documentMenuAnchorEl)}
                onClose={handleDocumentMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                  popper: {
                    modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
                  },
                }}
                PaperProps={{
                  sx: {
                    width: documentMenuAnchorEl ? documentMenuAnchorEl.offsetWidth : undefined,
                    minWidth: documentMenuAnchorEl ? documentMenuAnchorEl.offsetWidth : undefined,
                    maxWidth: documentMenuAnchorEl ? documentMenuAnchorEl.offsetWidth : undefined,
                    mt: 0,
                    boxShadow: 'none',
                    borderRadius: 0,
                    border: '1px solid #005b92',
                    py: 0.25,
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      py: 0.5,
                      px: 1.25,
                      color: '#005b92',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      '&:hover': {
                        backgroundColor: '#E8F4FC',
                      },
                    },
                  },
                }}
                MenuListProps={{
                  sx: { py: 0 },
                }}
              >
                <MuiMenuItem onClick={handleUploadDocument}>
                  {t('pages:documents.upload_document')}
                </MuiMenuItem>
                <MuiMenuItem onClick={handleUploadFromTemplate}>
                  {t('pages:documents.upload_from_template')}
                </MuiMenuItem>
                <MuiMenuItem onClick={handleSendEmail}>
                  {t('pages:emails.send_email')}
                </MuiMenuItem>
              </Menu>
            </>
          ) : currentTab === "emails" ? (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="text"
                onClick={() => setShowDrafts((prev) => !prev)}
                sx={{
                  textTransform: "none",
                  color: "#2B80B6 !important",
                  fontWeight: 500,
                  padding: "6px 20px",
                  fontSize: "13px",
                  borderRadius: "0",
                  fontFamily: "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                }}
                startIcon={showDrafts ? <MailOutlineIcon /> : <EditNoteIcon />}
                size="small"
              >
                {showDrafts ? t('pages:emails.show_sent') : t('pages:emails.show_drafts')}
              </Button>
              <Button
                startIcon={<AddIcon />}
                onClick={() => navigate("/send-email", { state: { caseId: id } })}
                size="small"
                sx={actionButtonStyles}
              >
                {t('pages:emails.send_email')}
              </Button>
            </Box>
          ) : (
            <Button
              sx={actionButtonStyles}
              onClick={handleButtonClick}
              startIcon={<AddIcon />}
            >
              {currentTab === "timesheets" ? t('pages:timesheets.new_timesheet') : ""}
              {currentTab === "tasks" ? t('pages:tasks.new_task') : ""}
              {currentTab === "disbursements" ? t('pages:disbursements.new_disbursement') : ""}
            </Button>
          )}
        </Box>
      ) : currentTab === "calendar" ? (
        <Box sx={{ padding: 0, margin: 0 }}>
          <Button
            onClick={handleMenuClick}
            endIcon={<ArrowDropDownIcon />}
            startIcon={
              <Box display="flex" alignItems="center">
                <AddIcon />
                <Box ml={1} />
              </Box>
            }
            sx={actionButtonStyles}
          >
            {t('pages:calendar.add_to_calendar')}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              popper: {
                modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
              },
            }}
            PaperProps={{
              sx: {
                width: anchorEl ? anchorEl.offsetWidth : undefined,
                minWidth: anchorEl ? anchorEl.offsetWidth : undefined,
                maxWidth: anchorEl ? anchorEl.offsetWidth : undefined,
                mt: 0,
                boxShadow: 'none',
                borderRadius: 0,
                border: '1px solid #005b92',
                py: 0.25,
                '& .MuiMenuItem-root': {
                  fontSize: '12px',
                  py: 0.5,
                  px: 1.25,
                  color: '#005b92',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  '&:hover': {
                    backgroundColor: '#E8F4FC',
                  },
                },
              },
            }}
            MenuListProps={{
              sx: { py: 0 },
            }}
          >
            <MuiMenuItem onClick={handleCreateAppointment}>
              {t('pages:calendar.create_appointment')}
            </MuiMenuItem>
            <MuiMenuItem onClick={handleCreateHearing}>
              {t('pages:calendar.create_hearing')}
            </MuiMenuItem>
          </Menu>
        </Box>
      ) : currentTab === "notes" ? (
        <Button
          sx={actionButtonStyles}
          startIcon={<AddIcon />}
          onClick={handleOpenNotes}
        >
          {t('pages:notes.add_note')}
        </Button>
      ) : currentTab === "finance" ? (
        <Box sx={{ display: "flex", gap: 2 }}>
          {financeActiveTab === 1 ? (
            <>
              <Button
                sx={actionButtonStyles}
                onClick={handleMenuOpen}
                endIcon={<ArrowDropDownIcon />}
                startIcon={
                  <Box display="flex" alignItems="center">
                    <AddIcon />
                    <Box ml={1} />
                  </Box>
                }
              >
                {t('pages:documents.new_document')}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                  popper: {
                    modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
                  },
                }}
                PaperProps={{
                  sx: {
                    width: anchorEl ? anchorEl.offsetWidth : undefined,
                    minWidth: anchorEl ? anchorEl.offsetWidth : undefined,
                    maxWidth: anchorEl ? anchorEl.offsetWidth : undefined,
                    mt: 0,
                    boxShadow: 'none',
                    borderRadius: 0,
                    border: '1px solid #005b92',
                    py: 0.25,
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      py: 0.5,
                      px: 1.25,
                      color: '#005b92',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      '&:hover': {
                        backgroundColor: '#E8F4FC',
                      },
                    },
                  },
                }}
                MenuListProps={{
                  sx: { py: 0 },
                }}
              >
                <MuiMenuItem onClick={handleCreateInvoice}>{t('pages:finance.invoice')}</MuiMenuItem>
                <MuiMenuItem onClick={handleCreateCredit}>{t('pages:finance.credit')}</MuiMenuItem>
                <MuiMenuItem onClick={handleCreateProvisionNote}>
                  {t('pages:finance.provision_note')}
                </MuiMenuItem>
              </Menu>
            </>
          ) : null}
          {financeActiveTab === 2 ? (
            <Button
              startIcon={<AddIcon />}
              onClick={handleRegisterClientPayment}
              sx={actionButtonStyles}
            >
              {t('pages:finance.register_client_payments')}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default BreadcrumbSection;